import {
    DTOCardFormField,
} from 'app/types/form.types'
import {
    CollectionServiceType, ContractCurrency,
    DeliveryServiceType, LocationType, ProductType, RegionSecondLevel,
    SimplifiedCQuoteReason, SimplifiedCQuoteStatus,
} from 'app/enums/label.enums'
import {
    SimplifiedCQuote,
} from '../SimplifiedCQuotes.types'

export default ({
    shortenMode,
    getAllowedValues,
    disabled,
    originCountry,
    destinationCountry,
}: {
    shortenMode: boolean,
    disabled: boolean,
    getAllowedValues: (e: Record<string, string>) => Record<string, string>,
    originCountry: number,
    destinationCountry: number,
}): DTOCardFormField<SimplifiedCQuote>[] => {
    const quoteInformationFields = [
        {
            id: 'quoteInformation-column1',
            fields: [
                {
                    name: 'simplifiedQuoteNumber',
                    labelKey: 'AccountQuotes.quoteNumber',
                    componentName: 'InputSingleline',
                    disabled: true,
                },
                {
                    name: 'productType',
                    labelKey: 'BaseLease.productType',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(ProductType),
                    disabled: true,
                },
                {
                    name: 'document',
                    labelKey: 'Attachment.filename',
                    componentName: 'FileDownloadLink',
                },
                {
                    name: 'createdOn',
                    componentName: 'DateTimeSelect',
                    disabled: true,
                    labelKey: 'Accounts.createdOn',
                },
                {
                    name: 'createdBy',
                    componentName: 'InputSingleline',
                    labelKey: 'RegionalGroups.createdBy',
                    disabled: true,
                },
            ],
        },
        {
            id: 'quoteInformation-column2',
            fields: [
                {
                    name: 'status',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(SimplifiedCQuoteStatus),
                    labelKey: 'AccountQuotes.laneStatus',
                    disabled,
                    useDropdown: true,
                    required: true,
                },
                {
                    name: 'reason',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(SimplifiedCQuoteReason),
                    labelKey: 'LaneFees.rejectedReason',
                    disabled,
                    useDropdown: true,
                    required: true,
                },
                {
                    name: 'reasonComment',
                    componentName: 'LongText',
                    disabled,
                    required: true,
                    minRows: 4,
                    labelKey: 'AccountQuotes.reasonComment',
                },
            ],
        },
        {
            id: 'quoteInformation-column3',
            fields: [
                {
                    name: 'originRegion',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(RegionSecondLevel),
                    disabled: true,
                    labelKey: 'AccountQuotes.originRegionL2',
                },
                {
                    name: 'originCountry',
                    componentName: 'CountryDataSelector',
                    labelKey: 'AccountQuotes.originCountry',
                    disabled: true,
                },
                {
                    name: 'originState',
                    componentName: 'StateSelector',
                    labelKey: 'AccountQuotes.originState',
                    disabled: true,
                    country: originCountry,
                },
                {
                    name: 'originAirport',
                    filterLocationType: LocationType.AIRPORT,
                    componentName: 'LocationObjectSelector',
                    labelKey: 'AccountQuotes.originIATA',
                    disabled: true,
                },
            ],
        },
        {
            id: 'quoteInformation-column4',
            fields: [
                {
                    name: 'destinationRegion',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(RegionSecondLevel),
                    disabled: true,
                    labelKey: 'AccountQuotes.destinationRegionL2',
                },
                {
                    name: 'destinationCountry',
                    componentName: 'CountryDataSelector',
                    labelKey: 'AccountQuotes.destinationCountry',
                    disabled: true,
                },
                {
                    name: 'destinationState',
                    componentName: 'StateSelector',
                    labelKey: 'AccountQuotes.destinationState',
                    disabled: true,
                    country: destinationCountry,
                },
                {
                    name: 'destinationAirport',
                    filterLocationType: LocationType.AIRPORT,
                    componentName: 'LocationObjectSelector',
                    labelKey: 'AccountQuotes.destinationIATA',
                    disabled: true,
                },
            ],
        },
    ]
    const generalFields = [
        {
            id: 'general-column1',
            fields: [
                {
                    name: 'currency',
                    labelKey: 'AccountQuotes.currency',
                    allowedValues: getAllowedValues(ContractCurrency),
                    componentName: 'EnumSelector',
                    disabled: true,
                },
                {
                    name: 'rentalDays',
                    componentName: 'IntegerInput',
                    min: 5,
                    max: 30,
                    required: true,
                    labelKey: 'LaneFeesUpdateDto.rentalDays',
                    disabled: true,
                },
                {
                    name: 'logisticsDays',
                    componentName: 'IntegerInput',
                    min: 0,
                    max: 30,
                    disabled: true,
                    labelKey: 'LaneFeesUpdateDto.logisticsDays',
                },
            ],
        },
        {
            id: 'general-column2',
            fields: [
                {
                    name: 'companyName',
                    componentName: 'InputSingleline',
                    disabled: true,
                    labelKey: 'AccountQuotes.customerCompanyName',
                },
                {
                    name: 'companyAddress',
                    labelKey: 'AccountQuotes.companyAddress',
                    componentName: 'LongText',
                    minRows: 4,
                    disabled: true,
                },
            ],
        },
    ]
    const deliveryOptionsFields = [{
        id: 'deliveryOptions-column1',
        fields: [
            {
                name: 'deliveryServiceType',
                labelKey: 'AccountLane.deliveryServiceType',
                componentName: 'EnumSelector',
                allowedValues: getAllowedValues(DeliveryServiceType),
                showInColumns: true,
                disabled: true,
            },
            {
                name: 'collectionServiceType',
                labelKey: 'AccountLane.collectionServiceType',
                componentName: 'EnumSelector',
                allowedValues: getAllowedValues(CollectionServiceType),
                showInColumns: true,
                disabled: true,
            },
        ],
    }]
    const contactDetailsFields = [
        {
            id: 'contactDetails-column1',
            fields: [{
                name: 'skyCellContact',
                componentName: 'UserObjectSelector',
                required: true,
                labelKey: 'AccountQuotes.skycellContact',
                disabled: true,
            }],
        },
        {
            id: 'contactDetails-column2',
            fields: [{
                name: 'customerContactNameAndSurname',
                componentName: 'InputSingleline',
                required: true,
                labelKey: 'AccountQuotes.contactNameAndSurname',
                disabled: true,
            }],
        },
        {
            id: 'contactDetails-column3',
            fields: [{
                name: 'customerContactEmail',
                componentName: 'InputSingleline',
                required: true,
                labelKey: 'AccountQuotes.contactEmail',
                disabled: true,
            }],
        },
    ]

    return [
        {
            id: 'quoteInformation',
            title: 'Quote Information',
            className: 'quoteInformation',
            fields: quoteInformationFields,
        } as DTOCardFormField<SimplifiedCQuote>,
        ...(!shortenMode ? [
            {
                id: 'general',
                title: 'General',
                className: 'general',
                fields: generalFields,
            },
            {
                id: 'deliveryOptions',
                title: 'Delivery Options',
                className: 'deliveryOptions',
                fields: deliveryOptionsFields,
            },
            {
                id: 'contactDetails',
                title: 'ContactDetails',
                className: 'contactDetails',
                fields: contactDetailsFields,
            },
        ] : []) as DTOCardFormField<SimplifiedCQuote>[],
    ]
}
